import { create } from 'zustand';
import {
  Character,
  StatType,
  generateAuctionPool,
  getCharacterById,
  getCharacterStatValue,
  getCharactersByIds,
  getNextBattleStat,
  getRandomStat,
  getAllVerses,
  normalizeStat,
} from '@/data/characters';
import type { Match, MatchPlayer, AuctionRecord, RoundRecord } from '@/lib/multiplayer';
import type { BattleFormat, CpuDifficulty, GameSettings } from '@/types/game';
import { load, save } from '@/lib/storage';

// Exclusions and role picks are remembered on this device.
const EXCLUDED_KEY = 'auctionExcludedCharacters';
const ROLES_KEY = 'auctionRolesByVerse';

export type GamePhase = 'home' | 'cpu-setup' | 'lobby' | 'auction' | 'battle' | 'results' | 'spectate';
export type GameMode = 'pvp' | 'cpu';
export type GameResult = 'player' | 'opponent' | 'draw' | null;
export type { BattleFormat, CpuDifficulty, GameSettings } from '@/types/game';

interface PlayerState {
  id: string;
  name: string;
  coins: number;
  team: Character[];
  currentBid: number;
  bidLocked: boolean;
  selectedCharacter: Character | null;
  selectionLocked: boolean;
  roundsWon: number;
  minimumBid: number;
}

interface AuctionRound {
  character: Character;
  playerBid: number;
  opponentBid: number;
  winner: 'player' | 'opponent' | 'tie';
}

interface BattleRound {
  stat: StatType;
  playerCharacter: Character | null;
  opponentCharacter: Character | null;
  playerStatValue: number;
  opponentStatValue: number;
  winner: 'player' | 'opponent' | 'tie';
}

interface GameState {
  // Game phase
  phase: GamePhase;
  mode: GameMode;
  roomCode: string | null;
  matchId: string | null;
  spectatorMatchId: string | null;
  playerId: string | null;
  opponentId: string | null;
  playerRole: 'host' | 'guest' | null;
  
  // Settings
  settings: GameSettings;

  // Result override (used for auction forfeits/draws)
  resultOverride: GameResult;
  
  // Players
  player: PlayerState;
  opponent: PlayerState;
  
  // Auction state
  auctionPool: Character[];
  currentAuctionIndex: number;
  auctionHistory: AuctionRound[];
  
  // Battle state
  currentStat: StatType | null;
  currentBattleRound: number;
  battleHistory: BattleRound[];
  // Tired character IDs (server-authoritative, set by applyBattleHistorySnapshot)
  tiredPlayerId: string | null;
  tiredOpponentId: string | null;
  
  // Timer
  timeRemaining: number;
  playerBoosts: {
    rerollUsed: boolean;
    doubleWinUsed: boolean;
    doubleWinArmed: boolean;
    extraTimeUsed: boolean;
    statBoostUsed: boolean;
    statBoostPct: number;
    statBoostPctRecord: number; // permanent record of boost % purchased (never reset)
    weakenUsed: boolean;
    weakenPct: number;
    weakenPctRecord: number; // permanent record of weaken % purchased (never reset)
    // Permanent "ever used" flags — never reset by server, used by ResultsScreen
    rerollEverUsed: boolean;
    doubleWinEverUsed: boolean;
    extraTimeEverUsed: boolean;
    statBoostEverUsed: boolean;
    weakenEverUsed: boolean;
  };
  opponentBoosts: {
    rerollUsed: boolean;
    doubleWinUsed: boolean;
    doubleWinArmed: boolean;
    extraTimeUsed: boolean;
    statBoostUsed: boolean;
    statBoostPct: number;
    weakenUsed: boolean;
    weakenPct: number;
  };
  auctionTimerStartedAt: string | null;
  realtimeEnabled: boolean;
  lastBoostEvent: null;
  lastLocalBidAt: number | null;
  lastLocalBidLockAt: number | null;
  lastLocalSelectionLockAt: number | null;
  bidVersion: number;
  stateVersion: number;
  useV2: boolean;
  
  // Actions
  setPhase: (phase: GamePhase) => void;
  startGame: (mode: GameMode) => void;
  setRoomCode: (code: string) => void;
  setSpectatorMatchId: (matchId: string | null) => void;
  setMultiplayerContext: (context: {
    matchId: string;
    playerId: string;
    playerRole: 'host' | 'guest';
  }) => void;
  resetMultiplayerGameState: () => void;
  clearMultiplayerContext: () => void;
  applyMatchSnapshot: (match: Match) => void;
  applyPlayersSnapshot: (players: MatchPlayer[]) => void;
  applyAuctionHistorySnapshot: (records: AuctionRecord[]) => void;
  applyBattleHistorySnapshot: (records: RoundRecord[]) => void;
  setResultOverride: (result: GameResult | null) => void;
  updateSettings: (settings: Partial<GameSettings>) => void;
  setBidVersion: (version: number) => void;
  setStateVersion: (version: number) => void;
  
  // Auction actions
  setPlayerBid: (amount: number) => void;
  setOpponentBid: (amount: number) => void;
  lockPlayerBid: () => void;
  unlockPlayerBid: () => void;
  lockOpponentBid: (amount: number) => void;
  unlockOpponentBid: () => void;
  resolveAuction: (options?: { penalizeLoser?: boolean }) => void;
  addAuctionRound: (round: AuctionRound) => void;
  nextAuction: () => void;
  
  // Battle actions
  selectCharacter: (character: Character) => void;
  lockSelection: () => void;
  lockOpponentSelection: (character: Character) => void;
  resolveBattle: (serverWins?: { playerWins: number; opponentWins: number }) => void;
  nextBattleRound: () => void;
  
  // Timer
  setTimeRemaining: (time: number) => void;
  setCurrentStat: (stat: StatType) => void;
  purchaseReroll: () => boolean;
  purchaseDoubleWin: () => boolean;
  purchaseExtraTime: () => boolean;
  purchaseStatBoost: (percent: 5 | 10) => boolean;
  purchaseWeaken: (percent: 5 | 10) => boolean;
  cpuApplyOpponentBoost: (type: 'stat_boost' | 'weaken' | 'double_win', percent?: 5 | 10) => void;
  applyTieBreaker: (winner: 'player' | 'opponent') => void;
  applyAuctionTieWinner: (winner: 'player' | 'opponent', character: Character) => void;
  
  // Reset
  resetGame: () => void;
  startRematch: () => void;
}

const STARTING_COINS = 5000;
const DEFAULT_VERSE = getAllVerses()[0] ?? 'Naruto';

const makeId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const initialPlayerState = (name: string): PlayerState => ({
  id: makeId(),
  name,
  coins: STARTING_COINS,
  team: [],
  currentBid: 0,
  bidLocked: false,
  selectedCharacter: null,
  selectionLocked: false,
  roundsWon: 0,
  minimumBid: 0,
});

const initialSettings: GameSettings = {
  verse: DEFAULT_VERSE,
  bidTimer: 20,
  battleFormat: 3,
  startingCoins: STARTING_COINS,
  auctionCount: 10,
  cpuDifficulty: 'hard',
  hiddenStat: false,
  rebidMode: false,
  coinRewards: false,
  coinRewardAmount: 500,
  excludedCharacters: load<string[]>(EXCLUDED_KEY, []),
  rolesByVerse: load<Record<string, string[]>>(ROLES_KEY, {}),
};

const initialPlayerBoosts = (): GameState['playerBoosts'] => ({
  rerollUsed: false,
  doubleWinUsed: false,
  doubleWinArmed: false,
  extraTimeUsed: false,
  statBoostUsed: false,
  statBoostPct: 0,
  statBoostPctRecord: 0,
  weakenUsed: false,
  weakenPct: 0,
  weakenPctRecord: 0,
  rerollEverUsed: false,
  doubleWinEverUsed: false,
  extraTimeEverUsed: false,
  statBoostEverUsed: false,
  weakenEverUsed: false,
});

const initialOpponentBoosts = (): GameState['opponentBoosts'] => ({
  rerollUsed: false,
  doubleWinUsed: false,
  doubleWinArmed: false,
  extraTimeUsed: false,
  statBoostUsed: false,
  statBoostPct: 0,
  weakenUsed: false,
  weakenPct: 0,
});

export const useGameStore = create<GameState>((set, get) => ({
  phase: 'home',
  mode: 'cpu',
  roomCode: null,
  matchId: null,
  spectatorMatchId: null,
  playerId: null,
  opponentId: null,
  playerRole: null,
  settings: { ...initialSettings },
  resultOverride: null,
  player: initialPlayerState('You'),
  opponent: initialPlayerState('CPU'),
  auctionPool: [],
  currentAuctionIndex: 0,
  auctionHistory: [],
  currentStat: null,
  currentBattleRound: 0,
  battleHistory: [],
  tiredPlayerId: null,
  tiredOpponentId: null,
  timeRemaining: 20,
  playerBoosts: {
    rerollUsed: false,
    doubleWinUsed: false,
    doubleWinArmed: false,
    extraTimeUsed: false,
    statBoostUsed: false,
    statBoostPct: 0,
    statBoostPctRecord: 0,
    weakenUsed: false,
    weakenPct: 0,
    weakenPctRecord: 0,
    rerollEverUsed: false,
    doubleWinEverUsed: false,
    extraTimeEverUsed: false,
    statBoostEverUsed: false,
    weakenEverUsed: false,
  },
  opponentBoosts: {
    rerollUsed: false,
    doubleWinUsed: false,
    doubleWinArmed: false,
    extraTimeUsed: false,
    statBoostUsed: false,
    statBoostPct: 0,
    weakenUsed: false,
    weakenPct: 0,
  },
  auctionTimerStartedAt: null,
  realtimeEnabled: false,
  lastBoostEvent: null,
  lastLocalBidAt: null,
  lastLocalBidLockAt: null,
  lastLocalSelectionLockAt: null,
  bidVersion: 0,
  stateVersion: 0,
  useV2: true, // always use v2 server-authoritative path for PvP

  setPhase: (phase) => set({ phase }),
  setBidVersion: (version: number) => set({ bidVersion: version }),
  setStateVersion: (version: number) => set({ stateVersion: version }),

  startGame: (mode) => {
    const { settings } = get();
    const pool = generateAuctionPool(settings.auctionCount, settings.verse, settings.excludedCharacters);
    
    set({
      mode,
      phase: 'auction',
      auctionPool: pool,
      currentAuctionIndex: 0,
      auctionHistory: [],
      resultOverride: null,
      player: { ...initialPlayerState('You'), coins: settings.startingCoins },
      opponent: { ...initialPlayerState(mode === 'cpu' ? 'CPU' : 'Opponent'), coins: settings.startingCoins },
      timeRemaining: settings.bidTimer,
      currentStat: null,
      currentBattleRound: 0,
      battleHistory: [],
      tiredPlayerId: null,
      tiredOpponentId: null,
      playerBoosts: {
        rerollUsed: false,
        doubleWinUsed: false,
        doubleWinArmed: false,
        extraTimeUsed: false,
        statBoostUsed: false,
        statBoostPct: 0,
        statBoostPctRecord: 0,
        weakenUsed: false,
        weakenPct: 0,
        weakenPctRecord: 0,
        rerollEverUsed: false,
        doubleWinEverUsed: false,
        extraTimeEverUsed: false,
        statBoostEverUsed: false,
        weakenEverUsed: false,
      },
      opponentBoosts: {
        rerollUsed: false,
        doubleWinUsed: false,
        doubleWinArmed: false,
        extraTimeUsed: false,
        statBoostUsed: false,
        statBoostPct: 0,
        weakenUsed: false,
        weakenPct: 0,
      },
      auctionTimerStartedAt: null,
      lastBoostEvent: null,
      lastLocalBidAt: null,
      lastLocalBidLockAt: null,
      bidVersion: 0,
      stateVersion: 0,
    });
  },

  setRoomCode: (code) => set({ roomCode: code }),
  setSpectatorMatchId: (matchId) => set({ spectatorMatchId: matchId }),

  setMultiplayerContext: ({ matchId, playerId, playerRole }) =>
    set((state) => ({
      matchId,
      playerId,
      playerRole,
      mode: 'pvp',
      phase: state.phase === 'lobby' ? 'lobby' : state.phase,
      auctionPool: [],
      currentAuctionIndex: 0,
      auctionHistory: [],
      battleHistory: [],
      currentStat: null,
      currentBattleRound: 0,
      tiredPlayerId: null,
      tiredOpponentId: null,
      resultOverride: null,
      timeRemaining: state.settings.bidTimer,
      auctionTimerStartedAt: null,
      lastBoostEvent: null,
      lastLocalBidAt: null,
      lastLocalBidLockAt: null,
      lastLocalSelectionLockAt: null,
      bidVersion: 0,
      stateVersion: 0,
      player: { ...initialPlayerState('You'), coins: state.settings.startingCoins },
      opponent: { ...initialPlayerState('Opponent'), coins: state.settings.startingCoins },
      playerBoosts: initialPlayerBoosts(),
      opponentBoosts: initialOpponentBoosts(),
    })),

  resetMultiplayerGameState: () =>
    set((state) => ({
      auctionPool: [],
      currentAuctionIndex: 0,
      auctionHistory: [],
      battleHistory: [],
      currentStat: null,
      currentBattleRound: 0,
      tiredPlayerId: null,
      tiredOpponentId: null,
      resultOverride: null,
      timeRemaining: state.settings.bidTimer,
      auctionTimerStartedAt: null,
      lastBoostEvent: null,
      lastLocalBidAt: null,
      lastLocalBidLockAt: null,
      lastLocalSelectionLockAt: null,
      bidVersion: 0,
      stateVersion: 0,
      player: { ...initialPlayerState('You'), coins: state.settings.startingCoins },
      opponent: { ...initialPlayerState('Opponent'), coins: state.settings.startingCoins },
      playerBoosts: initialPlayerBoosts(),
      opponentBoosts: initialOpponentBoosts(),
    })),

  clearMultiplayerContext: () =>
    set({
      roomCode: null,
      matchId: null,
      playerId: null,
      opponentId: null,
      playerRole: null,
      mode: 'cpu',
      realtimeEnabled: false,
      bidVersion: 0,
      stateVersion: 0,
    }),

  applyMatchSnapshot: (match) =>
    set((state) => {
      const auctionPoolFromIds =
        match.auction_pool_ids && match.auction_pool_ids.length > 0
          ? getCharactersByIds(match.auction_pool_ids)
          : null;  // auction_pool_ids is the only source of truth now
      const phase: GamePhase =
        match.status === 'waiting'
          ? 'lobby'
          : match.status === 'auction'
          ? 'auction'
          : match.status === 'battle'
          ? 'battle'
          : 'results';

      const nextSettings = {
        verse: match.verse,
        bidTimer: match.bid_timer,
        battleFormat: match.battle_format as BattleFormat,
        startingCoins: match.starting_coins,
        auctionCount: match.auction_pool_ids?.length
          ? (match.auction_pool_ids.length as GameSettings['auctionCount'])
          : state.settings.auctionCount,
        cpuDifficulty: state.settings.cpuDifficulty,
        hiddenStat: Boolean(match.hidden_stat),
        rebidMode: Boolean(match.rebid_mode),
        coinRewards: Boolean(match.coin_rewards),
        coinRewardAmount: match.coin_reward_amount ?? 500,
        // The pool already reflects the host's exclusions; only the role pick is needed here.
        excludedCharacters: state.settings.excludedCharacters,
        rolesByVerse: match.battle_roles?.length
          ? { ...state.settings.rolesByVerse, [match.verse]: match.battle_roles }
          : state.settings.rolesByVerse,
      };

      // For finished matches: derive result from roundsWon if available
      // (players snapshot may have arrived first with correct values),
      // otherwise fall back to existing resultOverride.
      // Special case: current_battle_round=0 means auction ended without enough chars.
      let nextResultOverride = state.resultOverride;
      if (match.status === 'finished') {
        if (match.current_battle_round === 0) {
          // Auction ended without enough characters — no battle happened
          nextResultOverride =
            state.player.team.length >= 3 && state.opponent.team.length < 3 ? 'player'
            : state.player.team.length < 3 && state.opponent.team.length >= 3 ? 'opponent'
            : 'draw';
        } else if (state.resultOverride === null) {
          // Battle ended — prefer the authoritative winner column written by the server.
          // This avoids the race where roundsWon is still 0:0 when applyMatchSnapshot fires.
          if (match.winner) {
            const isHost = state.playerRole === 'host';
            nextResultOverride =
              match.winner === 'draw' ? 'draw'
              : (match.winner === 'host') === isHost ? 'player'
              : 'opponent';
          } else {
            // Fallback: derive from current roundsWon.
            // Only trust roundsWon when BOTH players have non-zero data, OR the winner
            // clearly dominated (clean sweep). If opponent is still at initial 0 we
            // risk both clients deriving 'player' — leave null for players snapshot.
            const pWins = state.player.roundsWon;
            const oWins = state.opponent.roundsWon;
            // Safe to derive only when total wins is non-trivial or opponent is present
            if (pWins > 0 && oWins > 0) {
              nextResultOverride = pWins > oWins ? 'player' : oWins > pWins ? 'opponent' : 'draw';
            } else if (pWins + oWins > 0 && state.opponentId) {
              // opponentId is set once players snapshot has arrived — use as proxy
              // that opponent data is real, not initialState zeros
              nextResultOverride = pWins > oWins ? 'player' : oWins > pWins ? 'opponent' : 'draw';
            }
            // If still ambiguous, leave as null — players snapshot catch-up will set it
          }
        }
      }

      const isHostWaiting = state.playerRole === 'host' && match.status === 'waiting';
      const isFreshAuctionStart =
        match.status === 'auction' &&
        match.current_auction_index === 0 &&
        (state.phase === 'lobby' || state.phase === 'results' || state.resultOverride !== null);

      const phaseChanged = state.phase !== phase;
      const nextTimeRemaining = phaseChanged
        ? phase === 'auction'
          ? nextSettings.bidTimer
          : phase === 'battle'
          ? 15
          : state.timeRemaining
        : state.timeRemaining;

        const normalizedStat = normalizeStat(match.current_stat ?? null, match.verse);

        return {
          mode: 'pvp',
          phase,
          settings: isHostWaiting ? state.settings : nextSettings,
          auctionPool: isHostWaiting ? state.auctionPool : auctionPoolFromIds ?? [],
          currentAuctionIndex: match.current_auction_index,
          currentStat: normalizedStat,
          currentBattleRound: match.current_battle_round,
        timeRemaining: nextTimeRemaining,
        auctionTimerStartedAt: match.auction_timer_started_at ?? null,
        realtimeEnabled: true,
        lastBoostEvent: null,
        resultOverride: match.status === 'finished' ? nextResultOverride : null,

        ...(isFreshAuctionStart && {
          auctionHistory: [],
          battleHistory: [],
          tiredPlayerId: null,
          tiredOpponentId: null,
          lastLocalBidAt: null,
          lastLocalBidLockAt: null,
          lastLocalSelectionLockAt: null,
          bidVersion: 0,
          player: { ...initialPlayerState(state.player.name || 'You'), coins: nextSettings.startingCoins },
          opponent: { ...initialPlayerState(state.opponent.name || 'Opponent'), coins: nextSettings.startingCoins },
          playerBoosts: initialPlayerBoosts(),
          opponentBoosts: initialOpponentBoosts(),
        }),

        ...(state.useV2 && match.state_version !== undefined && { stateVersion: match.state_version }),
      };
    }),

  applyPlayersSnapshot: (players) =>
    set((state) => {
      if (!state.playerId) return {};
      const selfById = players.find((p) => p.id === state.playerId);
      const selfByRole = state.playerRole
        ? players.find((p) => p.player_role === state.playerRole)
        : undefined;
      const self = selfById ?? selfByRole;
      const opponent =
        self && players.length > 1
          ? players.find((p) => p.id !== self.id) ??
            (self.player_role ? players.find((p) => p.player_role !== self.player_role) : undefined)
          : undefined;
      if (!self) return {};

      const opponentBoosts = {
        rerollUsed: Boolean(opponent?.boost_reroll_used),
        doubleWinUsed: Boolean(opponent?.boost_double_win_used),
        doubleWinArmed: Boolean(opponent?.boost_double_win_armed),
        extraTimeUsed: Boolean(opponent?.boost_extra_time_used),
        statBoostUsed: Boolean(opponent?.boost_stat_boost_used),
        statBoostPct: opponent?.boost_stat_boost_pct ?? 0,
        weakenUsed: Boolean(opponent?.boost_weaken_used),
        weakenPct: opponent?.boost_weaken_pct ?? 0,
      };

      const playerBoosts = {
        rerollUsed: Boolean(self.boost_reroll_used),
        doubleWinUsed: Boolean(self.boost_double_win_used),
        doubleWinArmed: Boolean(self.boost_double_win_armed),
        extraTimeUsed: Boolean(self.boost_extra_time_used),
        statBoostUsed: Boolean(self.boost_stat_boost_used),
        statBoostPct: self.boost_stat_boost_pct ?? 0,
        // Keep the record fields from local state — the DB resets pct to 0 after a round
        // but we need to remember what was purchased for the ResultsScreen
        statBoostPctRecord: Math.max(state.playerBoosts.statBoostPctRecord, self.boost_stat_boost_pct ?? 0),
        weakenUsed: Boolean(self.boost_weaken_used),
        weakenPct: self.boost_weaken_pct ?? 0,
        weakenPctRecord: Math.max(state.playerBoosts.weakenPctRecord, self.boost_weaken_pct ?? 0),
        // "Ever used" flags latch true and never revert — server resets the regular flags
        // after each round but we need them for the match results screen.
        rerollEverUsed: state.playerBoosts.rerollEverUsed || Boolean(self.boost_reroll_used),
        doubleWinEverUsed: state.playerBoosts.doubleWinEverUsed || Boolean(self.boost_double_win_used),
        extraTimeEverUsed: state.playerBoosts.extraTimeEverUsed || Boolean(self.boost_extra_time_used),
        statBoostEverUsed: state.playerBoosts.statBoostEverUsed || Boolean(self.boost_stat_boost_used),
        weakenEverUsed: state.playerBoosts.weakenEverUsed || Boolean(self.boost_weaken_used),
      };

      const now = Date.now();
        const preserveLocalBid =
          state.mode === 'pvp' &&
          !state.player.bidLocked &&
          state.lastLocalBidAt !== null &&
          now - state.lastLocalBidAt < 4000 &&  // extended: submitBidV2 can be slow
          state.player.currentBid > 0 &&
          (self.current_bid ?? 0) < state.player.currentBid; // snapshot is stale/behind

        const preserveLocalLock =
          state.mode === 'pvp' &&
          state.player.bidLocked &&
          state.lastLocalBidLockAt !== null &&
          now - state.lastLocalBidLockAt < 5000 &&
          self.bid_locked === false;

        // Protect against DB snapshot overwriting local selectionLocked=true
        // before the server has processed the lock. Without this, the "BOTH
        // SELECTED" effect sees locked=false and never advances to comparison.
        // Guard against DB snapshot overwriting the local selectionLocked=true before the
        // server has finished processing quickLockSelectionV2. Window is 10 s to cover slow
        // connections; resolveBattle(serverWins) explicitly clears lastLocalSelectionLockAt.
        const preserveSelectionLock =
          state.mode === 'pvp' &&
          state.player.selectionLocked &&
          state.lastLocalSelectionLockAt !== null &&
          now - state.lastLocalSelectionLockAt < 10000 &&
          self.selection_locked === false;

      const selfTeam = self.team_ids?.length ? getCharactersByIds(self.team_ids) : [];
      const opponentTeam = opponent?.team_ids?.length ? getCharactersByIds(opponent.team_ids) : null;

      return {
        resultOverride: state.resultOverride,
        opponentId: opponent?.id ?? state.opponentId,
        player: {
          ...state.player,
          name: 'You',
          coins: self.coins_remaining,
          team: selfTeam,
          currentBid: preserveLocalBid ? state.player.currentBid : self.current_bid,
          bidLocked: preserveLocalLock ? true : self.bid_locked,
          selectedCharacter:
            preserveSelectionLock
              // DB snapshot may lag — keep the character the player actually chose locally
              ? state.player.selectedCharacter
              : self.selection_locked && state.player.selectionLocked
              // Both server and local agree player is locked — trust server's character
              ? (self.selected_character_id && selfTeam
                  ? selfTeam.find((c) => c.id === self.selected_character_id) ?? null
                  : null)
              : self.selection_locked && !state.player.selectionLocked
              // Server shows locked (stale from previous round) but player is NOT
              // locked locally — preserve any local selection the player has made
              // this round, otherwise fall back to server for reconnection scenarios.
              ? (state.player.selectedCharacter ?? (self.selected_character_id && selfTeam
                  ? selfTeam.find((c) => c.id === self.selected_character_id) ?? null
                  : null))
              : // Neither locked — preserve local selection
                state.player.selectedCharacter ?? null,
          selectionLocked: preserveSelectionLock ? true : self.selection_locked,
          roundsWon: self.rounds_won,
          minimumBid: self.minimum_bid ?? 0,
        },
        opponent: opponent
          ? {
              ...state.opponent,
              name: opponent.display_name ?? 'Opponent',
              coins: opponent.coins_remaining,
              team: opponentTeam ?? [],
              currentBid: opponent.current_bid,
              bidLocked: opponent.bid_locked,
              selectedCharacter:
                opponent.selected_character_id && opponentTeam
                  ? opponentTeam.find((c) => c.id === opponent.selected_character_id) ?? null
                  : null,
              selectionLocked: opponent.selection_locked,
              roundsWon: opponent.rounds_won,
              minimumBid: opponent.minimum_bid ?? 0,
            }
          : {
              ...state.opponent,
              name: state.mode === 'pvp' ? 'Opponent' : state.opponent.name,
            },
        playerBoosts,
        ...(state.useV2 && self.bid_version !== undefined && { bidVersion: self.bid_version }),
        opponentBoosts,
      };
    }),

  applyAuctionHistorySnapshot: (records) =>
    set((state) => {
      const isHost = state.playerRole !== 'guest';
      return {
        auctionHistory: records.map((record) => ({
          character: record.character_data,
          playerBid: isHost ? record.host_bid : record.guest_bid,
          opponentBid: isHost ? record.guest_bid : record.host_bid,
          winner:
            record.winner === 'tie'
              ? 'tie'
              : (record.winner === 'host') === isHost
              ? 'player'
              : 'opponent',
        })),
      };
    }),

  applyBattleHistorySnapshot: (records) =>
    set((state) => {
      // Never apply a shorter round list — round history only ever grows.
      // If a slow fetchRounds() query from before round N resolved completes
      // after a faster query that already included round N, discard it so
      // battleHistory never reverts to a previous state and shows a wrong winner.
      if (records.length < state.battleHistory.length) return {};

      const isHost = state.playerRole !== 'guest';
      const history = records.map((record) => {
          // character_data may be stored in the DB (newer rows) or need resolving by ID (older rows)
          const hostChar = record.host_character_data ?? (record.host_character_id ? getCharacterById(record.host_character_id) : null);
          const guestChar = record.guest_character_data ?? (record.guest_character_id ? getCharacterById(record.guest_character_id) : null);
          return {
            stat: record.stat_type,
            playerCharacter: isHost ? hostChar : guestChar,
            opponentCharacter: isHost ? guestChar : hostChar,
            playerStatValue: isHost ? record.host_stat_value : record.guest_stat_value,
            opponentStatValue: isHost ? record.guest_stat_value : record.host_stat_value,
            winner:
              record.winner === 'tie'
                ? 'tie'
                : (record.winner === 'host') === isHost
                ? ('player' as const)
                : ('opponent' as const),
          };
        });

      // Compute tired character IDs from the last round
      const lastHistoryRound = history.length > 0 ? history[history.length - 1] : null;
      const newTiredPlayerId = lastHistoryRound?.playerCharacter?.id ?? null;
      const newTiredOpponentId = lastHistoryRound?.opponentCharacter?.id ?? null;

      return {
        battleHistory: history,
        tiredPlayerId: newTiredPlayerId,
        tiredOpponentId: newTiredOpponentId,
        // A new round was written to match_rounds — the selection is resolved on the
        // server. Clear lastLocalSelectionLockAt so the preserveSelectionLock guard
        // lifts and the next players snapshot can reset selection_locked=false on
        // the guest client. Without this, the guest's lock guard stays active for
        // 10 s and causes the "BOTH SELECTED" effect to fire immediately on the next
        // selection phase, producing out-of-sync battle state.
        lastLocalSelectionLockAt: null,
        // Do NOT derive roundsWon from history record count — double-win gives 2 points
        // for a single round win so counting history entries always undercounts.
        // roundsWon is authoritatively set by applyPlayersSnapshot (match_players.rounds_won).
      };
    }),

  setResultOverride: (result) => set({ resultOverride: result }),

  updateSettings: (newSettings) => set((state) => {
    if (newSettings.excludedCharacters) save(EXCLUDED_KEY, newSettings.excludedCharacters);
    if (newSettings.rolesByVerse) save(ROLES_KEY, newSettings.rolesByVerse);
    return { settings: { ...state.settings, ...newSettings } };
  }),

  setPlayerBid: (amount) => set((state) => ({
    player: { ...state.player, currentBid: Math.min(amount, state.player.coins) },
    lastLocalBidAt: Date.now(), // stamp every time so preserveLocalBid window stays fresh
  })),

  setOpponentBid: (amount) => set((state) => ({
    opponent: { ...state.opponent, currentBid: amount },
  })),

  lockPlayerBid: () => set((state) => ({
    player: { ...state.player, bidLocked: true },
    lastLocalBidLockAt: Date.now(),
  })),

  unlockPlayerBid: () => set((state) => ({
    player: { ...state.player, bidLocked: false },
    lastLocalBidLockAt: null,
  })),

  lockOpponentBid: (amount) => set((state) => ({
    opponent: { ...state.opponent, currentBid: amount, bidLocked: true },
  })),

  unlockOpponentBid: () => set((state) => ({
    opponent: { ...state.opponent, bidLocked: false },
  })),

  addAuctionRound: (round) => set((state) => ({
    auctionHistory: [...state.auctionHistory, round],
  })),

  resolveAuction: (options) => {
    const state = get();
    const currentCharacter = state.auctionPool[state.currentAuctionIndex];
    if (!currentCharacter) return; // safety: pool exhausted or invalid index
    const playerBid = state.player.currentBid;
    const opponentBid = state.opponent.currentBid;
    const penalizeLoser = Boolean(options?.penalizeLoser);

    let winner: 'player' | 'opponent' | 'tie';
    let newPlayerCoins = state.player.coins;
    let newOpponentCoins = state.opponent.coins;
    const newPlayerTeam = [...state.player.team];
    const newOpponentTeam = [...state.opponent.team];

    if (playerBid > opponentBid) {
      winner = 'player';
      newPlayerCoins = Math.max(0, newPlayerCoins - playerBid);
      newPlayerTeam.push(currentCharacter);
      if (penalizeLoser) newOpponentCoins = Math.max(0, newOpponentCoins - opponentBid);
    } else if (opponentBid > playerBid) {
      winner = 'opponent';
      newOpponentCoins = Math.max(0, newOpponentCoins - opponentBid);
      newOpponentTeam.push(currentCharacter);
      if (penalizeLoser) newPlayerCoins = Math.max(0, newPlayerCoins - playerBid);
    } else {
      winner = 'tie';
      // Tie: no one pays, no one wins
    }

    const auctionRound: AuctionRound = {
      character: currentCharacter,
      playerBid,
      opponentBid,
      winner,
    };

    set({
      player: {
        ...state.player,
        coins: newPlayerCoins,
        team: newPlayerTeam,
        currentBid: 0,
        bidLocked: false,
      },
      opponent: {
        ...state.opponent,
        coins: newOpponentCoins,
        team: newOpponentTeam,
        currentBid: 0,
        bidLocked: false,
      },
      auctionHistory: [...state.auctionHistory, auctionRound],
    });
  },

  nextAuction: () => {
    const state = get();
    const nextIndex = state.currentAuctionIndex + 1;

    const poolExhausted = nextIndex >= state.auctionPool.length;

    if (poolExhausted) {
      const playerHasEnough = state.player.team.length >= 3;
      const opponentHasEnough = state.opponent.team.length >= 3;

      if (playerHasEnough && opponentHasEnough) {
        set({
          phase: 'battle',
          resultOverride: null,
          currentStat: getNextBattleStat({
            verse: state.settings.verse,
            rolesByVerse: state.settings.rolesByVerse,
            usedStats: state.battleHistory.map((round) => round.stat),
            currentStat: state.currentStat,
            seed: `${state.currentAuctionIndex}:${state.auctionHistory.length}`,
          }),
          currentBattleRound: 1,
          timeRemaining: 15,
        });
      } else if (!playerHasEnough && !opponentHasEnough) {
        set({
          phase: 'results',
          resultOverride: 'draw',
        });
      } else {
        set({
          phase: 'results',
          resultOverride: playerHasEnough ? 'player' : 'opponent',
        });
      }
    } else {
      set({
        currentAuctionIndex: nextIndex,
        timeRemaining: state.settings.bidTimer,
      });
    }
  },

  selectCharacter: (character) => set((state) => ({
    player: { ...state.player, selectedCharacter: character },
  })),

  lockSelection: () => set((state) => ({
    player: { ...state.player, selectionLocked: true },
    lastLocalSelectionLockAt: Date.now(),
  })),

  lockOpponentSelection: (character) => set((state) => ({
    opponent: { ...state.opponent, selectedCharacter: character, selectionLocked: true },
  })),

  resolveBattle: (serverWins?: { playerWins: number; opponentWins: number }) => {
    const state = get();
    if (!state.currentStat) return;

    if (serverWins) {
      // PvP path: server-authoritative win counts are provided.
      // DO NOT append to battleHistory here — subscribeToBattleRounds fires for
      // both players and calls applyBattleHistorySnapshot to rebuild it from the
      // server's match_rounds table, guaranteeing identical scorecards on both clients.
      // Only update roundsWon and clear selections/boosts.
      // Set tiredPlayerId/tiredOpponentId immediately so the next selection phase
      // greys out the right characters without waiting for subscribeToBattleRounds.
      set({
        lastLocalSelectionLockAt: null, // round resolved, safe to let DB own selectionLocked again
        tiredPlayerId: state.player.selectedCharacter?.id ?? null,
        tiredOpponentId: state.opponent.selectedCharacter?.id ?? null,
        player: {
          ...state.player,
          roundsWon: serverWins.playerWins,
          selectedCharacter: null,
          selectionLocked: false,
        },
        opponent: {
          ...state.opponent,
          roundsWon: serverWins.opponentWins,
          selectedCharacter: null,
          selectionLocked: false,
        },
        playerBoosts: {
          ...state.playerBoosts,
          statBoostPct: 0,
          weakenPct: 0,
          doubleWinArmed: false, // server already consumed it
        },
        opponentBoosts: {
          ...state.opponentBoosts,
          statBoostPct: 0,
          weakenPct: 0,
          doubleWinArmed: false,
        },
      });
      return;
    }

    // CPU path: compute everything locally and append to battleHistory
    if (!state.player.selectedCharacter || !state.opponent.selectedCharacter) return;

    const basePlayerStatValue = getCharacterStatValue(state.player.selectedCharacter, state.currentStat);
    const playerStatValue =
      Math.round(basePlayerStatValue * (1 + state.playerBoosts.statBoostPct / 100) * (1 - state.opponentBoosts.weakenPct / 100));
    const baseOpponentStatValue = getCharacterStatValue(state.opponent.selectedCharacter, state.currentStat);
    const opponentStatValue =
      Math.round(baseOpponentStatValue * (1 + state.opponentBoosts.statBoostPct / 100) * (1 - state.playerBoosts.weakenPct / 100));

    let winner: 'player' | 'opponent' | 'tie';
    let newPlayerWins = state.player.roundsWon;
    let newOpponentWins = state.opponent.roundsWon;

    if (playerStatValue > opponentStatValue) {
      winner = 'player';
      newPlayerWins += state.playerBoosts.doubleWinArmed ? 2 : 1;
    } else if (opponentStatValue > playerStatValue) {
      winner = 'opponent';
      newOpponentWins += state.opponentBoosts.doubleWinArmed ? 2 : 1;
    } else {
      winner = 'tie';
    }

    const battleRound: BattleRound = {
      stat: state.currentStat,
      playerCharacter: state.player.selectedCharacter,
      opponentCharacter: state.opponent.selectedCharacter,
      playerStatValue,
      opponentStatValue,
      winner,
    };

    set({
      player: {
        ...state.player,
        roundsWon: newPlayerWins,
        selectedCharacter: null,
        selectionLocked: false,
      },
      opponent: {
        ...state.opponent,
        roundsWon: newOpponentWins,
        selectedCharacter: null,
        selectionLocked: false,
      },
      battleHistory: [...state.battleHistory, battleRound],
      playerBoosts: {
        ...state.playerBoosts,
        statBoostPct: 0,
        weakenPct: 0,
        // Disarm doubleWin after any resolved round (consumed on win, wasted on loss).
        // Only keep it armed if the round was a tie — applyTieBreaker will consume it.
        doubleWinArmed: winner === 'tie' ? state.playerBoosts.doubleWinArmed : false,
      },
      opponentBoosts: {
        ...state.opponentBoosts,
        statBoostPct: 0,
        weakenPct: 0,
        doubleWinArmed: winner === 'tie' ? state.opponentBoosts.doubleWinArmed : false,
      },
    });
  },

  nextBattleRound: () => {
    const state = get();
    const { battleFormat } = state.settings;

    // Check for winner
    if (state.player.roundsWon >= battleFormat || state.opponent.roundsWon >= battleFormat) {
      set({ phase: 'results', resultOverride: null });
      return;
    }

    // Safety cap: prevent infinite battle loops (max 2× expected rounds)
    const maxRounds = battleFormat * 4;
    if (state.currentBattleRound >= maxRounds) {
      const result: 'player' | 'opponent' | 'draw' =
        state.player.roundsWon > state.opponent.roundsWon ? 'player'
        : state.opponent.roundsWon > state.player.roundsWon ? 'opponent'
        : 'draw';
      set({ phase: 'results', resultOverride: result });
      return;
    }

    set({
      currentStat: getNextBattleStat({
        verse: state.settings.verse,
            rolesByVerse: state.settings.rolesByVerse,
        usedStats: state.battleHistory.map((round) => round.stat),
        currentStat: state.currentStat,
        seed: state.currentBattleRound + 1,
      }),
      currentBattleRound: state.currentBattleRound + 1,
      timeRemaining: 15,
    });
  },

  setTimeRemaining: (time) => set({ timeRemaining: time }),
  setCurrentStat: (stat) => set({ currentStat: stat }),

  purchaseReroll: () => {
    const state = get();
    if (state.mode !== 'cpu') return false;
    if (state.playerBoosts.rerollUsed) return false;
    if (!state.currentStat) return false;
    const cost = 800;
    if (state.player.coins < cost) return false;
    const nextStat = getRandomStat({
      verse: state.settings.verse,
            rolesByVerse: state.settings.rolesByVerse,
      exclude: state.currentStat,
    });
    set({
      player: { ...state.player, coins: state.player.coins - cost },
      currentStat: nextStat,
      playerBoosts: { ...state.playerBoosts, rerollUsed: true, rerollEverUsed: true },
    });
    return true;
  },

  purchaseDoubleWin: () => {
    const state = get();
    if (state.mode !== 'cpu') return false;
    if (state.playerBoosts.doubleWinUsed) return false;
    const cost = 2000;
    if (state.player.coins < cost) return false;
    set({
      player: { ...state.player, coins: state.player.coins - cost },
      playerBoosts: {
        ...state.playerBoosts,
        doubleWinUsed: true,
        doubleWinArmed: true,
        doubleWinEverUsed: true,
      },
    });
    return true;
  },

  purchaseExtraTime: () => {
    const state = get();
    if (state.mode !== 'cpu') return false;
    if (state.playerBoosts.extraTimeUsed) return false;
    const cost = 750;
    if (state.player.coins < cost) return false;
    set({
      player: { ...state.player, coins: state.player.coins - cost },
      timeRemaining: state.timeRemaining + 5,
      playerBoosts: { ...state.playerBoosts, extraTimeUsed: true, extraTimeEverUsed: true },
    });
    return true;
  },

  purchaseStatBoost: (percent) => {
    const state = get();
    if (state.mode !== 'cpu') return false;
    if (state.playerBoosts.statBoostUsed) return false;
    const cost = percent === 5 ? 500 : 1000;
    if (state.player.coins < cost) return false;
    set({
      player: { ...state.player, coins: state.player.coins - cost },
      playerBoosts: {
        ...state.playerBoosts,
        statBoostUsed: true,
        statBoostPct: percent,
        statBoostPctRecord: percent,
        statBoostEverUsed: true,
      },
    });
    return true;
  },

  purchaseWeaken: (percent) => {
    const state = get();
    if (state.mode !== 'cpu') return false;
    if (state.playerBoosts.weakenUsed) return false;
    const cost = percent === 5 ? 600 : 1200;
    if (state.player.coins < cost) return false;
    set({
      player: { ...state.player, coins: state.player.coins - cost },
      playerBoosts: {
        ...state.playerBoosts,
        weakenUsed: true,
        weakenPct: percent,
        weakenPctRecord: percent,
        weakenEverUsed: true,
      },
    });
    return true;
  },

  cpuApplyOpponentBoost: (type, percent) => {
    const state = get();
    if (state.mode !== 'cpu') return;
    const costs = { stat_boost: percent === 10 ? 1000 : 500, weaken: percent === 10 ? 1200 : 600, double_win: 2000 };
    const cost = costs[type];
    if (state.opponent.coins < cost) return;
    if (type === 'stat_boost' && state.opponentBoosts.statBoostUsed) return;
    if (type === 'weaken' && state.opponentBoosts.weakenUsed) return;
    if (type === 'double_win' && state.opponentBoosts.doubleWinUsed) return;
    set({
      opponent: { ...state.opponent, coins: Math.max(0, state.opponent.coins - cost) },
      opponentBoosts: {
        ...state.opponentBoosts,
        ...(type === 'stat_boost' && { statBoostUsed: true, statBoostPct: percent ?? 5 }),
        ...(type === 'weaken' && { weakenUsed: true, weakenPct: percent ?? 5 }),
        ...(type === 'double_win' && { doubleWinUsed: true, doubleWinArmed: true }),
      },
    });
  },

  applyTieBreaker: (winner) => {
    const state = get();
    if (state.battleHistory.length === 0) return;
    const lastIndex = state.battleHistory.length - 1;
    const lastRound = state.battleHistory[lastIndex];
    if (lastRound.winner !== 'tie') return;

    let newPlayerWins = state.player.roundsWon;
    let newOpponentWins = state.opponent.roundsWon;

    if (winner === 'player') {
      if (state.playerBoosts.doubleWinArmed) {
        newPlayerWins += 2;
      } else {
        newPlayerWins += 1;
      }
    } else {
      newOpponentWins += 1;
    }

    const updatedHistory = [...state.battleHistory];
    updatedHistory[lastIndex] = { ...lastRound, winner };

    set({
      player: { ...state.player, roundsWon: newPlayerWins },
      opponent: { ...state.opponent, roundsWon: newOpponentWins },
      battleHistory: updatedHistory,
      playerBoosts: {
        ...state.playerBoosts,
        // Always disarm after the tie resolves — consumed on win, wasted on loss.
        doubleWinArmed: false,
      },
    });
  },

  applyAuctionTieWinner: (winner, character) => {
    const state = get();
    const playerBid = state.player.currentBid;
    const opponentBid = state.opponent.currentBid;

    let newPlayerCoins = state.player.coins;
    let newOpponentCoins = state.opponent.coins;
    const newPlayerTeam = [...state.player.team];
    const newOpponentTeam = [...state.opponent.team];

    if (winner === 'player') {
      newPlayerCoins -= playerBid;
      newPlayerTeam.push(character);
    } else {
      newOpponentCoins -= opponentBid;
      newOpponentTeam.push(character);
    }

    const auctionRound: AuctionRound = {
      character,
      playerBid,
      opponentBid,
      winner,
    };

    const nextIndex = state.currentAuctionIndex + 1;
    const poolExhausted = nextIndex >= state.auctionPool.length;

    if (poolExhausted) {
      const playerHasEnough = newPlayerTeam.length >= 3;
      const opponentHasEnough = newOpponentTeam.length >= 3;

      if (playerHasEnough && opponentHasEnough) {
        set({
          player: {
            ...state.player,
            coins: newPlayerCoins,
            team: newPlayerTeam,
            currentBid: 0,
            bidLocked: false,
          },
          opponent: {
            ...state.opponent,
            coins: newOpponentCoins,
            team: newOpponentTeam,
            currentBid: 0,
            bidLocked: false,
          },
          auctionHistory: [...state.auctionHistory, auctionRound],
          phase: 'battle',
          resultOverride: null,
          currentAuctionIndex: nextIndex,
          currentStat: getNextBattleStat({
            verse: state.settings.verse,
            rolesByVerse: state.settings.rolesByVerse,
            usedStats: state.battleHistory.map((round) => round.stat),
            currentStat: state.currentStat,
            seed: `${nextIndex}:${state.auctionHistory.length}`,
          }),
          currentBattleRound: 1,
          timeRemaining: 15,
        });
      } else if (!playerHasEnough && !opponentHasEnough) {
        set({
          player: {
            ...state.player,
            coins: newPlayerCoins,
            team: newPlayerTeam,
            currentBid: 0,
            bidLocked: false,
          },
          opponent: {
            ...state.opponent,
            coins: newOpponentCoins,
            team: newOpponentTeam,
            currentBid: 0,
            bidLocked: false,
          },
          auctionHistory: [...state.auctionHistory, auctionRound],
          phase: 'results',
          resultOverride: 'draw',
          currentAuctionIndex: nextIndex,
        });
      } else {
        set({
          player: {
            ...state.player,
            coins: newPlayerCoins,
            team: newPlayerTeam,
            currentBid: 0,
            bidLocked: false,
          },
          opponent: {
            ...state.opponent,
            coins: newOpponentCoins,
            team: newOpponentTeam,
            currentBid: 0,
            bidLocked: false,
          },
          auctionHistory: [...state.auctionHistory, auctionRound],
          phase: 'results',
          resultOverride: playerHasEnough ? 'player' : 'opponent',
          currentAuctionIndex: nextIndex,
        });
      }
      return;
    }

    set({
      player: {
        ...state.player,
        coins: newPlayerCoins,
        team: newPlayerTeam,
        currentBid: 0,
        bidLocked: false,
      },
      opponent: {
        ...state.opponent,
        coins: newOpponentCoins,
        team: newOpponentTeam,
        currentBid: 0,
        bidLocked: false,
      },
      auctionHistory: [...state.auctionHistory, auctionRound],
      currentAuctionIndex: nextIndex,
      timeRemaining: state.settings.bidTimer,
    });
  },

  resetGame: () => set({
    phase: 'home',
    mode: 'cpu',
    roomCode: null,
    matchId: null,
    spectatorMatchId: null,
    playerId: null,
    opponentId: null,
    playerRole: null,
    settings: { ...initialSettings },
    resultOverride: null,
    player: initialPlayerState('You'),
    opponent: initialPlayerState('CPU'),
    auctionPool: [],
    currentAuctionIndex: 0,
    auctionHistory: [],
    currentStat: null,
    currentBattleRound: 0,
    battleHistory: [],
    tiredPlayerId: null,
    tiredOpponentId: null,
    timeRemaining: 20,
    playerBoosts: {
      rerollUsed: false,
      doubleWinUsed: false,
      doubleWinArmed: false,
      extraTimeUsed: false,
      statBoostUsed: false,
      statBoostPct: 0,
      statBoostPctRecord: 0,
      weakenUsed: false,
      weakenPct: 0,
      weakenPctRecord: 0,
      rerollEverUsed: false,
      doubleWinEverUsed: false,
      extraTimeEverUsed: false,
      statBoostEverUsed: false,
      weakenEverUsed: false,
    },
    opponentBoosts: {
      rerollUsed: false,
      doubleWinUsed: false,
      doubleWinArmed: false,
      extraTimeUsed: false,
      statBoostUsed: false,
      statBoostPct: 0,
      weakenUsed: false,
      weakenPct: 0,
    },
    auctionTimerStartedAt: null,
    realtimeEnabled: false,
    lastBoostEvent: null,
    lastLocalBidAt: null,
    lastLocalBidLockAt: null,
    lastLocalSelectionLockAt: null,
    bidVersion: 0,
    stateVersion: 0,
  }),

  startRematch: () => {
    const { mode, settings } = get();
    const pool = generateAuctionPool(settings.auctionCount, settings.verse, settings.excludedCharacters);
    
    set({
      phase: 'auction',
      auctionPool: pool,
      currentAuctionIndex: 0,
      auctionHistory: [],
      resultOverride: null,
      player: { ...initialPlayerState('You'), coins: settings.startingCoins },
      opponent: { ...initialPlayerState(mode === 'cpu' ? 'CPU' : 'Opponent'), coins: settings.startingCoins },
      timeRemaining: settings.bidTimer,
      currentStat: null,
      currentBattleRound: 0,
      battleHistory: [],
      tiredPlayerId: null,
      tiredOpponentId: null,
      playerBoosts: {
        rerollUsed: false,
        doubleWinUsed: false,
        doubleWinArmed: false,
        extraTimeUsed: false,
        statBoostUsed: false,
        statBoostPct: 0,
        statBoostPctRecord: 0,
        weakenUsed: false,
        weakenPct: 0,
        weakenPctRecord: 0,
        rerollEverUsed: false,
        doubleWinEverUsed: false,
        extraTimeEverUsed: false,
        statBoostEverUsed: false,
        weakenEverUsed: false,
      },
      opponentBoosts: {
        rerollUsed: false,
        doubleWinUsed: false,
        doubleWinArmed: false,
        extraTimeUsed: false,
        statBoostUsed: false,
        statBoostPct: 0,
        weakenUsed: false,
        weakenPct: 0,
      },
      auctionTimerStartedAt: null,
      realtimeEnabled: false,
      lastBoostEvent: null,
      lastLocalBidAt: null,
      lastLocalBidLockAt: null,
      lastLocalSelectionLockAt: null,
      bidVersion: 0,
      stateVersion: 0,
    });
  },
}));
